import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllInstructorsApi, getAllUsersApi, createInstructorApi, updateUserRoleApi, deleteUserApi } from '../../../api/apiServices';
import { useCourseStore } from '../../../context/CourseStoreContext';
import { useAuth } from '../../../context/useAuth';

export default function AdminInstructorsPage() {
  const { user: currentUser } = useAuth();
  const { courses } = useCourseStore();
  
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);

  // Add instructor form
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phoneNumber: '',
    bio: '',
    website: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  const originUrl = typeof window !== 'undefined' ? window.location.origin : 'https://learnhub.app';
  const defaultInviteLink = `${originUrl}/signup?role=instructor&ref=${currentUser?.referralCode || 'INST-INVITE'}`;

  const loadInstructors = async () => {
    try {
      setLoading(true);
      // Try dedicated instructors API
      const res = await getAllInstructorsApi();
      if (res?.status && res?.instructors) {
        setInstructors(res.instructors);
      } else {
        // Fallback to getAllUsers filtering for instructors
        const usersRes = await getAllUsersApi(1, 100);
        if (usersRes?.users) {
          setInstructors(usersRes.users.filter(u => u.role === 'instructor'));
        }
      }
    } catch (err) {
      console.warn("Could not fetch instructors via API, attempting users list:", err);
      try {
        const usersRes = await getAllUsersApi(1, 100);
        if (usersRes?.users) {
          setInstructors(usersRes.users.filter(u => u.role === 'instructor'));
        }
      } catch (innerErr) {
        console.error("Error loading users/instructors:", innerErr);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInstructors();
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(defaultInviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleCopyEmailDraft = () => {
    const draft = `Hello,\n\nYou have been invited to join LearnHub as an Instructor! Create and manage your courses, reach thousands of ambitious students, and track your performance.\n\nPlease complete your instructor registration here:\n${defaultInviteLink}\n\nWe look forward to having you on the team!\n- LearnHub Administration`;
    navigator.clipboard.writeText(draft);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2500);
  };

  const handleCreateInstructor = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (!formData.email.trim() || !formData.password.trim()) {
      setFormError('Email and password are required.');
      return;
    }
    if (formData.password.length < 6) {
      setFormError('Password must be at least 6 characters.');
      return;
    }

    try {
      setSubmitting(true);
      const res = await createInstructorApi(formData);
      if (res?.status) {
        setFormSuccess('Instructor created successfully!');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          phoneNumber: '',
          bio: '',
          website: '',
        });
        await loadInstructors();
        setTimeout(() => {
          setShowAddModal(false);
          setFormSuccess('');
        }, 1200);
      } else {
        setFormError(res?.message || 'Failed to create instructor.');
      }
    } catch (err) {
      console.error("Error adding instructor:", err);
      setFormError(err?.response?.data?.message || 'Failed to create instructor account.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDemoteToStudent = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to change ${userName || 'this instructor'}'s role to Student?`)) {
      return;
    }

    try {
      await updateUserRoleApi(userId, 'student');
      await loadInstructors();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to update role");
    }
  };

  const handleDeleteInstructor = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to remove instructor "${userName || 'this user'}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteUserApi(userId);
      await loadInstructors();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete instructor");
    }
  };

  // Filter instructors
  const filteredInstructors = instructors.filter(inst => {
    const q = search.toLowerCase();
    const fullName = `${inst.firstName || ''} ${inst.lastName || ''} ${inst.userName || ''}`.toLowerCase();
    const email = (inst.email || '').toLowerCase();
    return fullName.includes(q) || email.includes(q);
  });

  return (
    <div className="space-y-8">
      {/* Top Banner & Actions */}
      <div className="bg-gradient-to-r from-slate-900 via-teal-950 to-slate-900 rounded-3xl p-6 md:p-8 text-white shadow-xl relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 border border-teal-400/30 text-teal-300 text-xs font-bold mb-3">
              <i className="ri-team-line" /> Faculty & Instructor Management
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white">Instructors & Mentors</h1>
            <p className="text-slate-300 text-sm max-w-xl mt-1 leading-relaxed">
              Add qualified educators, manage teaching permissions, and distribute unique sign-up invitation links.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs transition-all border border-white/15 cursor-pointer backdrop-blur-sm shadow-sm active:scale-95"
            >
              <i className="ri-share-forward-line text-teal-300 text-base" />
              Invite Link
            </button>

            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs transition-all shadow-lg active:scale-95 cursor-pointer"
            >
              <i className="ri-user-add-line text-base" />
              Add Instructor
            </button>
          </div>
        </div>
      </div>

      {/* Shareable Invite Card Preview */}
      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm flex flex-col lg:flex-row items-center justify-between gap-6">
        <div className="space-y-1 text-left w-full lg:w-auto">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping" />
            <h3 className="text-sm font-bold text-slate-900">Direct Instructor Sign-up Link</h3>
          </div>
          <p className="text-xs text-slate-500">
            Anyone who registers using this link will automatically be granted the <strong className="text-teal-700">Instructor</strong> role.
          </p>
        </div>

        <div className="flex items-center gap-2 w-full lg:w-auto max-w-xl">
          <div className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono text-slate-700 truncate select-all">
            {defaultInviteLink}
          </div>
          <button
            type="button"
            onClick={handleCopyLink}
            className={`px-4 py-2 rounded-xl font-bold text-xs transition-all cursor-pointer inline-flex items-center gap-1.5 whitespace-nowrap shadow-xs ${
              copied
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-900 hover:bg-slate-800 text-white'
            }`}
          >
            <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'} />
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
      </div>

      {/* Instructors List Section */}
      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-teal-50 flex items-center justify-center text-teal-600 text-lg font-bold">
              <i className="ri-user-star-line" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Registered Instructors</h2>
              <p className="text-xs text-slate-500">
                {instructors.length} active instructor{instructors.length === 1 ? '' : 's'} on LearnHub
              </p>
            </div>
          </div>

          <div className="relative w-full sm:w-72">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
            />
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400 text-xs space-y-2">
            <div className="w-8 h-8 rounded-full border-2 border-teal-500 border-t-transparent animate-spin mx-auto" />
            <p>Loading instructor accounts...</p>
          </div>
        ) : filteredInstructors.length === 0 ? (
          <div className="py-14 text-center border border-dashed border-slate-200 rounded-2xl p-8">
            <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 text-xl mx-auto mb-3">
              <i className="ri-user-unfollow-line" />
            </div>
            <h3 className="text-sm font-bold text-slate-800">No instructors found</h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1 mb-4">
              {search ? 'No instructors matched your search query.' : 'No instructors have been added yet. Add an instructor or share the signup link!'}
            </p>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs rounded-xl transition-all inline-flex items-center gap-1.5 cursor-pointer shadow-sm"
            >
              <i className="ri-add-line" /> Add First Instructor
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredInstructors.map((inst) => {
              const displayName = (inst.firstName && inst.lastName)
                ? `${inst.firstName} ${inst.lastName}`
                : inst.userName || inst.email.split('@')[0];
              const instructorCourses = courses.filter(c => c.instructor?.name?.toLowerCase().includes(displayName.toLowerCase()));

              return (
                <div
                  key={inst.userId || inst._id || inst.email}
                  className="bg-slate-50 hover:bg-white border border-slate-200/80 hover:border-teal-300 rounded-2xl p-5 transition-all shadow-xs hover:shadow-md flex flex-col justify-between"
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-teal-600 to-emerald-500 text-white flex items-center justify-center font-bold text-sm shadow-sm flex-shrink-0">
                          {displayName.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-sm font-bold text-slate-900 truncate">{displayName}</h4>
                          <p className="text-[11px] text-slate-500 truncate">{inst.email}</p>
                        </div>
                      </div>

                      <span className="px-2.5 py-0.5 rounded-full bg-teal-100 text-teal-800 text-[10px] font-bold uppercase tracking-wider flex-shrink-0">
                        Instructor
                      </span>
                    </div>

                    {inst.bio && (
                      <p className="text-xs text-slate-600 line-clamp-2 italic bg-white/70 p-2.5 rounded-xl border border-slate-100">
                        &ldquo;{inst.bio}&rdquo;
                      </p>
                    )}

                    <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-200/60">
                      <span>Courses: <strong className="text-slate-800">{instructorCourses.length}</strong></span>
                      <span>Joined: <strong className="text-slate-700">{new Date(inst.createdAt || Date.now()).toLocaleDateString()}</strong></span>
                    </div>
                  </div>

                  <div className="pt-4 mt-4 border-t border-slate-200 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => handleDemoteToStudent(inst.userId || inst._id, displayName)}
                      className="text-[11px] font-semibold text-slate-600 hover:text-amber-600 transition-colors cursor-pointer"
                      title="Demote account to Student role"
                    >
                      Make Student
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDeleteInstructor(inst.userId || inst._id, displayName)}
                      className="text-[11px] font-semibold text-rose-600 hover:text-rose-700 transition-colors cursor-pointer flex items-center gap-1"
                    >
                      <i className="ri-delete-bin-line" /> Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal: Direct Add Instructor */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 md:p-8 shadow-2xl border border-slate-100 my-8">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-6">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center text-lg font-bold">
                  <i className="ri-user-add-line" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Add New Instructor</h3>
                  <p className="text-xs text-slate-500">Create an instructor account directly</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {formError && (
              <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl flex items-center gap-2">
                <i className="ri-error-warning-line text-sm" />
                {formError}
              </div>
            )}

            {formSuccess && (
              <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs rounded-xl flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-sm" />
                {formSuccess}
              </div>
            )}

            <form onSubmit={handleCreateInstructor} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">First Name</label>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="e.g. Adebayo"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="e.g. Ogunlesi"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Email Address</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="instructor@example.com"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-slate-700">Initial Password</label>
                  <button
                    type="button"
                    onClick={() => {
                      const randPass = 'Inst#' + Math.random().toString(36).slice(-6) + '!';
                      setFormData({ ...formData, password: randPass });
                    }}
                    className="text-[11px] font-semibold text-teal-600 hover:text-teal-700 cursor-pointer"
                  >
                    Generate Random
                  </button>
                </div>
                <input
                  type="text"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="At least 6 characters"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Bio / Headline</label>
                <textarea
                  rows={3}
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="e.g. Senior Full-Stack Developer with 8+ years experience in React & Node.js..."
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-800 rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer disabled:opacity-50"
                >
                  {submitting ? 'Creating...' : 'Create Instructor Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Invite Link & Share Generator */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 md:p-8 shadow-2xl border border-slate-100 my-8 space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center text-lg font-bold">
                  <i className="ri-share-forward-line" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Invite Instructor to Sign Up</h3>
                  <p className="text-xs text-slate-500">Send an invitation link or email draft</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowInviteModal(false)}
                className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {/* Link Box */}
            <div className="space-y-2">
              <label className="block text-xs font-bold text-slate-700">Dedicated Invitation Link</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={defaultInviteLink}
                  className="flex-1 px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-slate-800 select-all"
                />
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs rounded-xl shadow-xs transition-all cursor-pointer whitespace-nowrap"
                >
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
              <p className="text-[11px] text-slate-500">
                When instructors register via this link, their role is automatically assigned as <strong className="text-teal-700">Instructor</strong>.
              </p>
            </div>

            {/* Email Draft Section */}
            <div className="space-y-2 bg-slate-50 border border-slate-200/80 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                  <i className="ri-mail-line text-teal-600" /> Pre-written Email Template
                </span>
                <button
                  type="button"
                  onClick={handleCopyEmailDraft}
                  className="text-xs font-bold text-teal-600 hover:text-teal-700 cursor-pointer"
                >
                  {copiedEmail ? 'Copied Draft!' : 'Copy Email Text'}
                </button>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed italic bg-white p-3 rounded-xl border border-slate-100">
                &ldquo;You have been invited to join LearnHub as an Instructor! Create courses, reach students, and track earnings at {defaultInviteLink}&rdquo;
              </p>
            </div>

            {/* Share Buttons */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <a
                href={`mailto:?subject=Invitation to become an Instructor on LearnHub&body=Hi,%0D%0A%0D%0AYou are invited to join LearnHub as an instructor. Please sign up here:%0D%0A${encodeURIComponent(defaultInviteLink)}`}
                className="flex items-center justify-center gap-2 py-2.5 px-4 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all shadow-sm"
              >
                <i className="ri-mail-send-line" /> Send via Email
              </a>

              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`Hi, you have been invited to join LearnHub as an Instructor! Sign up here: ${defaultInviteLink}`)}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl transition-all shadow-sm"
              >
                <i className="ri-whatsapp-line" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
